import { toCartAddress } from '@shared/shop-api/address/mapper'
import { toCartLine } from '@shared/shop-api/cart/lines/mapper'
import type { CartLine } from '@shared/shop-api/cart/lines/types'
import { toCartCost } from '@shared/shop-api/cost/mapper'
import { toDelivery } from '@shared/shop-api/delivery/mapper'
import {
  toCartDiscount,
  toCartDiscountAllocation,
} from '@shared/shop-api/discount/mapper'
import { toError } from '@shared/shop-api/error/mapper'
import type { CartErrorFragment } from '@shared/shop-api/error/types'
import { toWarning } from '@shared/shop-api/warning/mapper'
import type {
  CartFragment,
  CartWarningFragment,
} from '@shared/types/storefront/graphql'

export const toCart = (
  cart: CartFragment,
  warnings: CartWarningFragment[] = [],
  errors: CartErrorFragment[] = []
) => {
  const lines = cart.lines.items.map(toCartLine)
  const availablePromotions = toAvailablePromotions(lines)
  const mappedWarnings = warnings.map(toWarning)

  return {
    id: cart.id,
    buyerIdentity: {
      countryCode: cart.buyerIdentity.countryCode ?? undefined,
      email: cart.buyerIdentity.email ?? undefined,
      phone: cart.buyerIdentity.phone ?? undefined,
    },
    checkoutUrl: cart.checkoutUrl ?? undefined,
    cost: toCartCost(cart.cost),
    createdAt: new Date(cart.createdAt),
    delivery: {
      addresses: cart.delivery.addresses.map(toCartAddress),
      ...toDelivery(cart.deliveryGroups.items[0]),
    },
    lines,
    availablePromotions,
    discountWarning: mappedWarnings.find((warning) =>
      warning.code.startsWith('DISCOUNT_')
    ),
    merchandiseWarning: mappedWarnings.find((warning) =>
      warning.code.startsWith('MERCHANDISE_')
    ),
    errors: errors.map(toError),
    discounts: cart.discountCodes.map(toCartDiscount),
    discountAllocations: cart.discountAllocations.map(toCartDiscountAllocation),
    quantity: cart.lines.items.reduce((sum, line) => sum + line.quantity, 0),
  }
}

export const toAvailablePromotions = (lines: CartLine[]) => {
  const productsWithPromotion = lines.filter(
    (line) => !!line.variant.product?.attributes.promotedProducts?.length
  )
  const promotions = productsWithPromotion.flatMap(toPromotions)

  return promotions.filter((promotion) => {
    const hasPromotedProductInCart = lines.some((line) => {
      const isPromotionActive = lines.some(
        (activeLine) => activeLine.id === line.attributes.promotedBy
      )

      return (
        isPromotionActive &&
        line.variant.product &&
        promotion.items.includes(line.variant.product.id)
      )
    })

    return !hasPromotedProductInCart
  })
}

export const toPromotions = (line: CartLine) => {
  return line.variant.product.attributes.promotedProducts.map(
    (promotedProduct) => ({
      promotedByLineId: line.id,
      ...promotedProduct,
    })
  )
}
